/**
 * Garment measurements. An item is measured as a top or as bottoms, and the
 * category decides which, so the form only shows what applies. Values are
 * stored on the item's `measurements` object with the unit they were taken in:
 *   { "garment": "top", "unit": "in", "pit_to_pit": 21, "length": 28 }
 */

export type Garment = "top" | "bottom";
export type LengthUnit = "in" | "cm";

export const MEASUREMENT_FIELDS: Record<Garment, readonly string[]> = {
  top: ["pit_to_pit", "length", "sleeve", "shoulder"],
  bottom: ["waist", "inseam", "rise", "hip", "leg_opening", "length"],
};

// Checked against the subcategory first, then the category: "activewear" says
// nothing, "leggings" says bottoms.
const BOTTOM = /\b(bottoms?|pants?|jeans?|shorts?|leggings?|joggers?|skirts?|skorts?|trousers?|chinos?|sweatpants?|capris?|culottes?|overalls?|trunks|briefs)\b/;
const TOP = /\b(tops?|shirts?|t-shirts?|tees?|blouses?|polos?|sweaters?|hoodies?|sweatshirts?|pullovers?|cardigans?|jackets?|coats?|vests?|tanks?|tank tops?|blazers?|outerwear|camis?|tunics?|crop tops?|jerseys?|long.sleeve|bras?|bodysuits?)\b/;

function classify(text: string | null | undefined): Garment | null {
  if (!text) return null;
  const t = text.toLowerCase().replace(/[_]/g, " ");
  if (BOTTOM.test(t)) return "bottom";
  if (TOP.test(t)) return "top";
  return null;
}

/** Top or bottoms from what the item is; null when it can't tell (a dress, a hat). */
export function garmentOf(category: string | null | undefined, subcategory: string | null | undefined): Garment | null {
  return classify(subcategory) ?? classify(category);
}

export function unitFor(units: "imperial" | "metric"): LengthUnit {
  return units === "metric" ? "cm" : "in";
}

/** The stored measurements for one garment type, dropping blanks. */
export function measurementsRecord(garment: Garment, unit: LengthUnit, values: Record<string, number | null>): Record<string, string | number> {
  const out: Record<string, string | number> = { garment, unit };
  for (const key of MEASUREMENT_FIELDS[garment]) {
    const v = values[key];
    if (v !== null && v !== undefined && Number.isFinite(v) && v > 0) out[key] = v;
  }
  return out;
}

/** Whether a measurements object has any actual measurement in it. */
export function hasMeasurements(m: Record<string, unknown> | null | undefined): boolean {
  if (!m) return false;
  return Object.entries(m).some(([k, v]) => k !== "garment" && k !== "unit" && typeof v === "number");
}

/**
 * Weights are stored in grams, with the unit they were entered in (oz, lbs or
 * kg), which is also how they are shown. A workspace defaults to ounces
 * (imperial) or kilograms (metric).
 */
export const WEIGHT_UNITS = ["oz", "lbs", "kg"] as const;
export type WeightUnit = (typeof WEIGHT_UNITS)[number];

export const GRAMS_PER_OZ = 28.349523125;
const GRAMS_PER: Record<WeightUnit, number> = { oz: GRAMS_PER_OZ, lbs: GRAMS_PER_OZ * 16, kg: 1000 };
const DECIMALS: Record<WeightUnit, number> = { oz: 1, lbs: 2, kg: 3 };

export function defaultWeightUnit(units: "imperial" | "metric"): WeightUnit {
  return units === "metric" ? "kg" : "oz";
}

export function gramsFrom(value: number, unit: WeightUnit): number {
  return Math.round(value * GRAMS_PER[unit]);
}

/** The weight in `unit`, rounded to what that unit is typed in (0.1 oz, 0.01 lbs, 0.001 kg). */
export function valueIn(grams: number, unit: WeightUnit): number {
  const f = 10 ** DECIMALS[unit];
  return Math.round((grams / GRAMS_PER[unit]) * f) / f;
}

export function formatWeight(grams: number | null | undefined, unit: WeightUnit, locale = "en-US"): string | null {
  if (grams === null || grams === undefined) return null;
  const v = new Intl.NumberFormat(locale, { maximumFractionDigits: DECIMALS[unit] }).format(valueIn(grams, unit));
  return `${v} ${unit}`;
}

/**
 * Reads a weight typed as text into grams plus the value and unit to show it
 * in, or null. Needs a unit: "4.2 oz", "4.2 ounces", "1 lb 1.4 oz" (17.4 oz),
 * "1 pound 4.7 ounces", "1.2 kg", "250 g". A bare number is not a weight.
 * Used to carry weights typed into the old size-system field.
 */
export function parseWeightText(text: string | null | undefined): { grams: number; value: number; unit: WeightUnit } | null {
  if (!text) return null;
  const t = text.toLowerCase().replace(/,/g, ".");
  let oz = 0;
  let kg = 0;
  let found = false;
  for (const [, raw, word] of t.matchAll(/(\d+(?:\.\d+)?)\s*(kilograms?|kgs?|grams?|g|pounds?|lbs?|ounces?|oz)\b/g)) {
    const v = Number(raw);
    if (word!.startsWith("k")) kg += v;
    else if (word!.startsWith("g")) kg += v / 1000;
    else if (word!.startsWith("p") || word!.startsWith("l")) oz += v * 16;
    else oz += v;
    found = true;
  }
  if (!found) return null;
  // Imperial entries stay in total ounces; metric ones in kilograms.
  if (kg > 0 && oz === 0) {
    const value = Math.round(kg * 1000) / 1000;
    return { grams: gramsFrom(value, "kg"), value, unit: "kg" };
  }
  const value = Math.round((oz + (kg * 1000) / GRAMS_PER_OZ) * 10) / 10;
  return { grams: gramsFrom(value, "oz"), value, unit: "oz" };
}

/**
 * Weights are entered as two fields: pounds and ounces (imperial) or kilograms
 * and grams (metric). Either or both may be filled: 35 oz, 1.3 lbs, 1 lb 10 oz.
 */
export const WEIGHT_PARTS: Record<"imperial" | "metric", readonly [WeightUnit | "g", WeightUnit | "g"]> = {
  imperial: ["lbs", "oz"],
  metric: ["kg", "g"],
};

/** The two fields as typed → grams; null when both are empty. Throws on anything else. */
export function gramsFromParts(major: string, minor: string, units: "imperial" | "metric"): number | null {
  const read = (s: string) => {
    const t = s.trim().replace(",", ".");
    if (t === "") return null;
    const n = Number(t);
    if (!Number.isFinite(n) || n < 0) throw new Error("A weight must be a positive number");
    return n;
  };
  const a = read(major);
  const b = read(minor);
  if (a === null && b === null) return null;
  if (units === "metric") return Math.round((a ?? 0) * 1000 + (b ?? 0));
  const oz = Math.round(((a ?? 0) * 16 + (b ?? 0)) * 10) / 10;
  return gramsFrom(oz, "oz");
}

/** Grams → the two fields: whole pounds and the ounces left over (or kilograms and grams). */
export function partsFromGrams(grams: number | null, units: "imperial" | "metric"): { major: string; minor: string } {
  if (grams === null) return { major: "", minor: "" };
  if (units === "metric") {
    const kg = Math.floor(grams / 1000);
    const g = grams - kg * 1000;
    return { major: kg > 0 ? String(kg) : "", minor: g > 0 || kg === 0 ? String(g) : "" };
  }
  const total = valueIn(grams, "oz");
  const lbs = Math.floor(total / 16);
  const oz = Math.round((total - lbs * 16) * 10) / 10;
  return { major: lbs > 0 ? String(lbs) : "", minor: oz > 0 || lbs === 0 ? String(oz) : "" };
}

/** "4.2 oz", "1 lb 4.7 oz", "2 lbs"; "350 g", "1 kg 250 g". */
export function formatWeightParts(grams: number | null | undefined, units: "imperial" | "metric"): string | null {
  if (grams === null || grams === undefined) return null;
  const { major, minor } = partsFromGrams(grams, units);
  const [bigUnit, smallUnit] = units === "metric" ? ["kg", "g"] : [major === "1" ? "lb" : "lbs", "oz"];
  return [major ? `${major} ${bigUnit}` : "", minor ? `${minor} ${smallUnit}` : ""].filter(Boolean).join(" ") || `0 ${smallUnit}`;
}
